import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import {
  SplitButtonAction,
  SplitButtonGroup,
  SplitButtonLoadingAction,
  SplitButtonTrigger,
} from '@/components/patterns/split-button'
import LoadingButton from '@/components/ui/loading-button'
import { Badge } from '@/components/ui/badge'
import type { ButtonVariant } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import FulfillmentConfirmationSummary from '@/components/forms/Orders/FulfillmentConfirmationSummary'
import {
  defaultShouldNotifyCustomer,
  isCustomerFulfillmentNotificationAction,
  sourceAllowsCustomerFulfillmentNotifications,
} from '@/components/forms/Orders/fulfillment-notification'
import { showFulfillmentUpdateResult } from '@/components/forms/Orders/fulfillment-sync'
import {
  type FulfillmentAction,
  type FulfillmentActionTargetStatus,
  type FulfillmentWorkflowOrder,
  canMoveFulfilledOrderBack,
  getFulfillmentAction,
  getNextFulfillmentWorkflowAction,
  getFulfillmentWorkflowActions,
  isFulfilledOrder,
} from '@/components/forms/Orders/fulfillment-workflow'
import { DynamicIcon } from '@/components/ui/DynamicIcon'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderRefundStatus } from '@/lib/enums/SalesOrderRefundStatus'
import { useAppForm } from '@/lib/form'
import { useUpdateSalesOrderMutation } from '@/lib/services/sales-orders/sales-orders.query'
import type {
  SalesOrderFulfillmentStatus as FulfillmentStatus,
  SalesOrderListItem,
} from '@/lib/services/sales-orders/sales-orders.types'
import { cn, getErrorMessage } from '@/lib/utils'
import { ChevronDown, Info, Undo2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'

/** The order fields the fulfillment control reads: a full order and an index row both carry them. */
export type MarkFulfilledOrder = FulfillmentWorkflowOrder &
  Pick<
    SalesOrderListItem,
    | 'id'
    | 'fulfillment_due_date'
    | 'source_allows_customer_fulfillment_notifications'
    | 'purchaser'
    | 'refund_status'
    | 'cancelled_at'
    | 'customer_fulfillment_notified'
    | 'customer_ready_for_pickup_notified'
  >

export interface MarkFulfilledOrderAction {
  /** The confirmation dialog while a status change waits for it; render it next to the control. */
  dialogs: ReactNode
  isPending: boolean
  actions: FulfillmentAction[]
  label: string
  primaryAction: FulfillmentAction | null
  /**
   * Resolves once the order is updated; undefined when nothing is sent right away (already
   * fulfilled, a disabled action, or waiting for the confirmation dialog).
   */
  requestMarkFulfilled: (targetStatus?: FulfillmentActionTargetStatus) => Promise<void> | undefined
  secondaryActions: FulfillmentAction[]
}

type FulfillmentActionAppearance = {
  variant: ButtonVariant
  className?: string
}

type MarkFulfilledFormProps = {
  order: MarkFulfilledOrder
  condensed?: boolean
  fullWidth?: boolean
  className?: string
}

function isPickupOrder(order: Pick<SalesOrderListItem, 'fulfillment_type'>): boolean {
  return order.fulfillment_type === FulfillmentType.Pickup.Value
}

function isDeliveryOrder(order: Pick<SalesOrderListItem, 'fulfillment_type'>): boolean {
  return order.fulfillment_type === FulfillmentType.Delivery.Value
}

export function getMarkFulfilledActionLabel(
  order: FulfillmentWorkflowOrder,
  targetStatus: FulfillmentStatus | undefined
): string {
  if (isFulfilledOrder(order)) {
    return isDeliveryOrder(order) ? 'Delivered' : 'Picked Up'
  }

  const resolvedTargetStatus =
    targetStatus ??
    getNextFulfillmentWorkflowAction(order)?.targetStatus ??
    SalesOrderFulfillmentStatus.Fulfilled.Value

  return getFulfillmentAction(order, resolvedTargetStatus).label
}

function getFulfillmentActionAppearance(
  order: FulfillmentWorkflowOrder
): FulfillmentActionAppearance {
  if (isFulfilledOrder(order)) {
    return { variant: 'success', className: '' }
  }

  if (isPickupOrder(order)) {
    return { variant: 'fulfillmentPickup' }
  }

  if (isDeliveryOrder(order)) {
    return { variant: 'fulfillmentDelivery' }
  }

  return { variant: 'outline' }
}

export function useMarkFulfilledOrderAction(order: MarkFulfilledOrder): MarkFulfilledOrderAction {
  const { mutateAsync: updateSalesOrder, isPending } = useUpdateSalesOrderMutation()
  const [confirmTargetStatus, setConfirmTargetStatus] =
    useState<FulfillmentActionTargetStatus | null>(null)
  const [shouldNotifyCustomer, setShouldNotifyCustomer] = useState(true)
  const [customerDeliveryNote, setCustomerDeliveryNote] = useState('')
  const actions = getFulfillmentWorkflowActions(order)
  const primaryAction = getNextFulfillmentWorkflowAction(order)
  const confirmAction = confirmTargetStatus
    ? getFulfillmentAction(order, confirmTargetStatus)
    : null

  const updateOrderFulfillment = async ({
    action,
    sendCustomerFulfillmentNotification,
    deliveryNote,
  }: {
    action: FulfillmentAction
    sendCustomerFulfillmentNotification?: boolean
    deliveryNote?: string
  }): Promise<void> => {
    try {
      const updatedOrder = await updateSalesOrder({
        id: order.id,
        fulfillment_status: action.targetStatus,
        ...(sendCustomerFulfillmentNotification !== undefined
          ? { send_customer_fulfillment_notification: sendCustomerFulfillmentNotification }
          : {}),
        ...(deliveryNote ? { customer_delivery_note: deliveryNote } : {}),
      })
      setConfirmTargetStatus(null)
      setCustomerDeliveryNote('')
      showFulfillmentUpdateResult(updatedOrder, action.successMessage)
    } catch (error) {
      console.error(action.failureMessage, error)
      toast.error(action.failureMessage, {
        description: getErrorMessage(error),
      })
      throw error
    }
  }

  const requestMarkFulfilled = (
    targetStatus: FulfillmentActionTargetStatus | undefined = primaryAction?.targetStatus
  ): Promise<void> | undefined => {
    // A delivered or picked-up order only moves back, and only one that can.
    const movesBack = isFulfilledOrder(order)
    if (
      !targetStatus ||
      (movesBack &&
        (targetStatus === SalesOrderFulfillmentStatus.Fulfilled.Value ||
          !canMoveFulfilledOrderBack(order)))
    ) {
      return
    }

    const action = getFulfillmentAction(order, targetStatus)
    if (action.disabled) return
    const requiresConfirmation =
      movesBack ||
      isCustomerFulfillmentNotificationAction({
        order,
        nextFulfillmentStatus: targetStatus,
      })

    if (requiresConfirmation) {
      setShouldNotifyCustomer(
        defaultShouldNotifyCustomer({ order, nextFulfillmentStatus: targetStatus })
      )
      setCustomerDeliveryNote('')
      setConfirmTargetStatus(targetStatus)
      return
    }

    return updateOrderFulfillment({ action })
  }

  const isConfirmOpen = confirmTargetStatus !== null
  // Marking a fulfilled order not delivered or not picked up offers a correction under the same rule as every other
  // step's Notify the customer switch; the API emails or texts it as it does the delivered and picked-up messages.
  const movesBack = isConfirmOpen && isFulfilledOrder(order)
  const showNotificationSwitch =
    confirmTargetStatus !== null &&
    sourceAllowsCustomerFulfillmentNotifications(order) &&
    (movesBack ||
      isCustomerFulfillmentNotificationAction({
        order,
        nextFulfillmentStatus: confirmTargetStatus,
      }))
  const showCustomerNote =
    showNotificationSwitch &&
    isDeliveryOrder(order) &&
    Boolean(order.purchaser?.email) &&
    confirmTargetStatus === SalesOrderFulfillmentStatus.Fulfilled.Value
  const dialogs = confirmAction ? (
    <ConfirmationDialog
      open={isConfirmOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setConfirmTargetStatus(null)
          setCustomerDeliveryNote('')
        }
      }}
      title={`${confirmAction.label}?`}
      description={`Set this order ${movesBack ? 'back ' : ''}to ${confirmAction.statusLabel.toLowerCase()}.`}
      descriptionClassName="sr-only"
      confirmText={confirmAction.label}
      pendingText="Updating..."
      isPending={isPending}
      closeOnError={false}
      icon={<Info className="size-4" />}
      trigger={null}
      onConfirm={() =>
        updateOrderFulfillment({
          action: confirmAction,
          sendCustomerFulfillmentNotification: showNotificationSwitch
            ? shouldNotifyCustomer
            : undefined,
          deliveryNote:
            showCustomerNote && shouldNotifyCustomer ? customerDeliveryNote.trim() : undefined,
        })
      }
    >
      <FulfillmentConfirmationSummary
        statusLabel={confirmAction.statusLabel}
        statusText={movesBack ? <>Set status back to {confirmAction.statusLabel}</> : undefined}
        summaryText={
          confirmAction.targetStatus === SalesOrderFulfillmentStatus.Fulfilled.Value &&
          !order.can_move_fulfillment_back
            ? 'This action cannot be undone.'
            : null
        }
        icon={
          movesBack ? (
            <Undo2 className="size-4" />
          ) : (
            <DynamicIcon iconName={confirmAction.iconName} className="size-4" />
          )
        }
        notifyCustomer={shouldNotifyCustomer}
        onNotifyCustomerChange={setShouldNotifyCustomer}
        showNotificationSwitch={showNotificationSwitch}
        showCustomerNote={showCustomerNote}
        customerNote={customerDeliveryNote}
        onCustomerNoteChange={setCustomerDeliveryNote}
        disabled={isPending}
      />
    </ConfirmationDialog>
  ) : null

  return {
    dialogs,
    isPending,
    actions,
    label: getMarkFulfilledActionLabel(order, primaryAction?.targetStatus),
    primaryAction,
    requestMarkFulfilled,
    secondaryActions: actions.filter(
      (action) => action.targetStatus !== primaryAction?.targetStatus
    ),
  }
}

function MarkFulfilledForm({
  order,
  condensed = false,
  fullWidth = !condensed,
  className = '',
}: MarkFulfilledFormProps) {
  const { dialogs, isPending, label, primaryAction, requestMarkFulfilled, secondaryActions } =
    useMarkFulfilledOrderAction(order)
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false)
  const displayAction =
    primaryAction ?? getFulfillmentAction(order, SalesOrderFulfillmentStatus.Fulfilled.Value)

  const form = useAppForm({
    defaultValues: {
      fulfillment_status: displayAction.targetStatus,
    },
    validators: {
      onSubmitAsync: async () => {
        // Prevent submission if order is already completed
        if (isFulfilledOrder(order)) {
          return
        }

        return requestMarkFulfilled()
      },
    },
  })

  const buttonSize = condensed ? 'icon' : fullWidth ? 'sm' : 'default'
  const buttonClassName = condensed ? '' : fullWidth ? 'w-full' : ''
  const actionAppearance = getFulfillmentActionAppearance(order)
  const showSplitAction = !condensed && secondaryActions.length > 0

  if (order.refund_status === SalesOrderRefundStatus.Refunded.Value || order.cancelled_at) {
    return null
  }

  // A fulfilled order shows its status; one that can move back offers its earlier steps beside it.
  if (isFulfilledOrder(order) && (condensed || secondaryActions.length === 0)) {
    return (
      <Badge
        role="status"
        variant="success"
        size="md"
        className={cn(
          condensed ? 'size-9 p-0' : fullWidth ? 'h-[35px] w-full px-3.5' : 'h-10 px-4',
          className
        )}
        aria-label={label}
      >
        <DynamicIcon iconName={displayAction.iconName} className="size-4" />
        {condensed ? <span className="sr-only">{label}</span> : label}
      </Badge>
    )
  }

  // Link preloading can re-render this form during a touch. Keep these as elements:
  // nested component types remount visible controls and Safari can cancel navigation.
  const fulfilledButton = (
    <LoadingButton
      disabled={isFulfilledOrder(order) || displayAction.disabled}
      isPending={isPending}
      preserveChildren={false}
      type="submit"
      variant={actionAppearance.variant}
      size={buttonSize}
      className={buttonClassName}
    >
      <DynamicIcon iconName={displayAction.iconName} className="size-4" />
      {condensed ? '' : label}
    </LoadingButton>
  )

  const statusMenuContent = (
    <DropdownMenuContent align="end">
      <DropdownMenuLabel>Set Status</DropdownMenuLabel>
      {secondaryActions.map((action) => (
        <DropdownMenuItem
          key={action.targetStatus}
          disabled={isPending || action.disabled}
          onSelect={(event) => {
            event.preventDefault()
            Promise.resolve(requestMarkFulfilled(action.targetStatus)).catch(() => {})
          }}
        >
          <DynamicIcon iconName={action.iconName} className="size-4" />
          {action.menuLabel}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  )

  const fulfillmentSplitButton = (
    <SplitButtonGroup className={fullWidth ? 'w-full' : undefined}>
      <SplitButtonLoadingAction
        disabled={isFulfilledOrder(order) || displayAction.disabled}
        grow={fullWidth}
        isPending={isPending}
        preserveChildren={false}
        type="submit"
        variant={actionAppearance.variant}
        size={buttonSize}
      >
        <DynamicIcon iconName={displayAction.iconName} className="size-4" />
        {label}
      </SplitButtonLoadingAction>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SplitButtonTrigger
            type="button"
            variant={actionAppearance.variant}
            size={buttonSize}
            disabled={isPending}
            aria-label="More fulfillment actions"
          >
            <ChevronDown className="size-4" />
          </SplitButtonTrigger>
        </DropdownMenuTrigger>
        {statusMenuContent}
      </DropdownMenu>
    </SplitButtonGroup>
  )

  // A delivered or picked-up order that can move back keeps the split button, in its fulfilled color: its status and
  // the chevron both open the menu of its earlier steps.
  const fulfilledStatusMenu = (
    <SplitButtonGroup className={fullWidth ? 'w-full' : undefined}>
      <SplitButtonAction
        type="button"
        grow={fullWidth}
        variant={actionAppearance.variant}
        size={buttonSize}
        disabled={isPending}
        onClick={() => setIsStatusMenuOpen(true)}
      >
        <DynamicIcon iconName={displayAction.iconName} className="size-4" />
        {label}
      </SplitButtonAction>
      <DropdownMenu open={isStatusMenuOpen} onOpenChange={setIsStatusMenuOpen}>
        <DropdownMenuTrigger asChild>
          <SplitButtonTrigger
            type="button"
            variant={actionAppearance.variant}
            size={buttonSize}
            disabled={isPending}
            aria-label="More fulfillment actions"
          >
            <ChevronDown className="size-4" />
          </SplitButtonTrigger>
        </DropdownMenuTrigger>
        {statusMenuContent}
      </DropdownMenu>
    </SplitButtonGroup>
  )

  const fulfilledButtonWithTooltip = (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{fulfilledButton}</TooltipTrigger>
        <TooltipContent variant={isDeliveryOrder(order) ? 'periwinkle' : 'default'}>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )

  return (
    <>
      <form.AppForm>
        <form.Form className={className}>
          <form.AppField name="fulfillment_status">
            {() =>
              condensed
                ? fulfilledButtonWithTooltip
                : isFulfilledOrder(order)
                  ? fulfilledStatusMenu
                  : showSplitAction
                    ? fulfillmentSplitButton
                    : fulfilledButton
            }
          </form.AppField>
          <form.SubmitButton className="hidden">Submit</form.SubmitButton>
        </form.Form>
      </form.AppForm>
      {dialogs}
    </>
  )
}

export default MarkFulfilledForm
